import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import { isEmpty } from 'lodash'
import salesforce from '../../images/salesforce.png'
import hubspot from '../../images/hubspot.png'
import connectwiseOld from '../../images/connectwise.old.png'
import connectwise from '../../images/connectwise.png'
import {
  CRM,
  getCRMs,
  type CRMListState,
} from '../../modules/crmSelector/action'
import type { FormData } from '../../modules/crmAuth/crmConnectionActions'
import { getCRMFormData } from '../../modules/crmAuth/actions'
import { updateAppAlert } from '../../modules/appAlert/actions'
import type { AppAlertState } from '../../modules/appAlert/reducer'
import { PartnerType } from '../../types/partner'
import { isAdminRole } from '../../utils/isAdminRole'
import { themeType } from '../../utils/constants'
import { mergeDefaultCRMListWithState } from './mergeDefaultCRMListWithState'
import type { selectedCRM } from './CRMSelectorUI'
import type { CrmOnboardingRootState } from '../../modules/crmSelector/crmOnboardingState'
export interface ImageMapper {
  crm_type: string
  logo: string
  disabled: boolean
  selected: boolean
  oAuth: boolean
  handleClick?: () => void
}

type reqState = {
  listCRMFromState: CRMListState['list']
  crmFormData?: FormData
  partnerId: null | string | undefined
  crmId: string
}

const headerMessage: AppAlertState = {
  message: undefined,
  messageType: 'NONE',
}

const imgArray: ImageMapper[] = [
  {
    crm_type: 'salesforce',
    logo: salesforce,
    disabled: true,
    oAuth: true,
    selected: true,
  },
  {
    crm_type: 'hubspot',
    logo: hubspot,
    disabled: true,
    oAuth: true,
    selected: true,
  },
  {
    crm_type: 'connectwise',
    logo: connectwiseOld,
    disabled: true,
    oAuth: false,
    selected: true,
  },
]

export interface CRMSelectorProps {
  theme?: themeType
  helpTitle?: string
}

const CRMSelectorUINew = lazy(() => import('./CRMSelectorUI'))

const oAuthCRMs = ['salesforce', 'hubspot']
export const CRMSelector: React.FC<CRMSelectorProps> = (
  props: CRMSelectorProps
) => {
  const { theme, helpTitle } = props
  const user = useSelector(
    (state: CrmOnboardingRootState) => state.userProfile.userProfile
  )
  const isAdmin = isAdminRole(user)
  const { listCRMFromState, crmFormData, partnerId, crmId } =
    useSelector((state: CrmOnboardingRootState) => {
      const partnerType = isAdmin ? PartnerType.Admin : PartnerType.User
      return {
        listCRMFromState: state.crmSelector.list,
        crmFormData: state.CRMAuth.crmFormData,
        partnerId: state.AccountData?.[partnerType]?.partnerData?.partnerId
          ? state.AccountData?.[partnerType]?.partnerData?.partnerId
          : null,
        crmId: state.AccountData?.[partnerType]?.partnerData?.crmId || '',
      }
    })
  const [selectedCRM, setCRM] = useState<selectedCRM>()

  const dispatch =
    useDispatch<ThunkDispatch<CrmOnboardingRootState, unknown, AnyAction>>()

  const [imageMapper, setImageMapper] = useState<ImageMapper[]>(imgArray)

  //On Page Load
  useEffect(() => {
    if (partnerId != null) {
      if (!isEmpty(crmId) && isEmpty(crmFormData)) {
        dispatch(getCRMFormData(crmId, { errorPresentation: 'none' }))
      }
      if (isEmpty(listCRMFromState)) {
        dispatch(getCRMs())
      }
    }
  }, [partnerId, listCRMFromState, crmId, crmFormData])

  //For handling the click event
  const handleClick = (e: CRM | string) => {
    setCRM(e)
    setImageMapper(previousState => {
      return previousState.map(c =>
        c.crm_type !== (typeof e !== 'string' ? e.crm_type : e)
          ? { ...c, selected: true }
          : { ...c, selected: false }
      ) as Array<ImageMapper>
    })
    dispatch(updateAppAlert(headerMessage))
  }

  const handleCRMFormData = () => {
    if (crmFormData && listCRMFromState.length !== 0) {
      setImageMapper(previousState =>
        previousState.map(img =>
          img.crm_type === crmFormData.crm_type
            ? { ...img, selected: false, disabled: true }
            : { ...img, selected: true, disabled: true }
        )
      )
      if (!oAuthCRMs.includes(crmFormData.crm_type)) {
        const creds = listCRMFromState.filter(
          crm => crm.crm_type === crmFormData.crm_type
        )[0]['credential_fields']
        setCRM({
          ...({
            crm_type: crmFormData.crm_type,
            credential_fields: creds.map(c => {
              const fill = crmFormData.creds?.find(
                cred => cred.auth_key_name === c.key
              )
              return {
                ...c,
                value: fill ? fill['auth_key_value'] : '',
              }
            }),
          } as CRM),
        })
      } else {
        setCRM(crmFormData.crm_type)
      }
    }
  }

  //Whenever State changes
  useMemo(async () => {
    await setImageMapper(prevState =>
      mergeDefaultCRMListWithState(prevState, listCRMFromState, handleClick)
    )
    handleCRMFormData()
  }, [listCRMFromState, crmFormData])

  useEffect(() => {
    const newImageMapper = imageMapper.map(a =>
      a.crm_type === 'connectwise' ? { ...a, logo: connectwise } : a
    )
    setImageMapper(newImageMapper)
  }, [])

  return (
    <Suspense fallback={<div></div>}>
      <CRMSelectorUINew
        imageMapper={imageMapper}
        selectedCRM={selectedCRM}
        crmId={crmId}
        isAdmin={isAdmin}
        theme={theme}
        helpTitle={helpTitle}
      />
    </Suspense>
  )
}
